import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  criarManutencaoProgramada,
  listarManutencoesProgramadas,
  aceitarManutencaoProgramada,
  rejeitarManutencaoProgramada,
  prorrogarManutencaoProgramada,
  apagarManutencaoProgramada,
  suspenderOcorrenciaProgramada,
  pausarSerieManutencao,
  retomarSerieManutencao,
  ManutencaoPayload,
  ManutencaoItem,
  ListarManutencoesParams,
} from "./services";

interface ManutencaoState {
  status: "idle" | "loading" | "success" | "error";
  error: string | null;

  // ─── Fila de aprovação (uso administrativo, nível > 6) ──────────────────
  lista: ManutencaoItem[];
  totalRegistros: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
  statusFiltro: string;
  listaStatus: "idle" | "loading" | "error";
  listaError: string | null;

  actionLoadingId: number | null;

  // ─── Visão de andamento por escopo (usuário convencional, nível < 6) ────
  // Estado separado de propósito: essa lista é somente-leitura e filtrada
  // por loja/regional, não deve se misturar com a fila de aprovação acima
  // (que é filtrada por status e usada pelas ações Aceitar/Rejeitar/Apagar).
  listaEscopo: ManutencaoItem[];
  totalRegistrosEscopo: number;
  paginaEscopo: number;
  tamanhoPaginaEscopo: number;
  totalPaginasEscopo: number;
  listaEscopoStatus: "idle" | "loading" | "error";
  listaEscopoError: string | null;
}

const initialState: ManutencaoState = {
  status: "idle",
  error: null,

  lista: [],
  totalRegistros: 0,
  pagina: 1,
  tamanhoPagina: 20,
  totalPaginas: 0,
  statusFiltro: "",
  listaStatus: "idle",
  listaError: null,

  actionLoadingId: null,

  listaEscopo: [],
  totalRegistrosEscopo: 0,
  paginaEscopo: 1,
  tamanhoPaginaEscopo: 20,
  totalPaginasEscopo: 0,
  listaEscopoStatus: "idle",
  listaEscopoError: null,
};

export const agendarManutencao = createAsyncThunk<
  any,
  ManutencaoPayload,
  { rejectValue: string }
>("manutencao/agendar", async (payload, { rejectWithValue }) => {
  try {
    const data = await criarManutencaoProgramada(payload);
    return data;
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao processar o agendamento.");
  }
});

export const listarManutencoes = createAsyncThunk<
  any,
  ListarManutencoesParams,
  { rejectValue: string }
>("manutencao/listar", async (params, { rejectWithValue }) => {
  try {
    const data = await listarManutencoesProgramadas(params);
    return data;
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao listar manutenções.");
  }
});

// Visão de andamento por escopo (loja ou regional) — usuário convencional.

export const aceitarManutencao = createAsyncThunk<
  { id: number },
  { id: number; instrucoesCliente: string },
  { rejectValue: string }
>(
  "manutencao/aceitar",
  async ({ id, instrucoesCliente }, { rejectWithValue }) => {
    try {
      await aceitarManutencaoProgramada(id, instrucoesCliente);
      return { id };
    } catch (err: any) {
      return rejectWithValue(err.message || "Erro ao aceitar manutenção.");
    }
  },
);

export const rejeitarManutencao = createAsyncThunk<
  { id: number },
  { id: number; justificativa: string },
  { rejectValue: string }
>("manutencao/rejeitar", async ({ id, justificativa }, { rejectWithValue }) => {
  try {
    await rejeitarManutencaoProgramada(id, justificativa);
    return { id };
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao rejeitar manutenção.");
  }
});

export const prorrogarManutencao = createAsyncThunk<
  { id: number },
  { id: number; novoHorarioFim: string },
  { rejectValue: string }
>(
  "manutencao/prorrogar",
  async ({ id, novoHorarioFim }, { rejectWithValue }) => {
    try {
      await prorrogarManutencaoProgramada(id, novoHorarioFim);
      return { id };
    } catch (err: any) {
      return rejectWithValue(err.message || "Erro ao prorrogar manutenção.");
    }
  },
);

export const apagarManutencao = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: string }
>("manutencao/apagar", async (id, { rejectWithValue }) => {
  try {
    await apagarManutencaoProgramada(id);
    return { id };
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao apagar manutenção.");
  }
});

export const suspenderOcorrencia = createAsyncThunk<
  { id: number },
  { id: number; justificativa: string },
  { rejectValue: string }
>(
  "manutencao/suspenderOcorrencia",
  async ({ id, justificativa }, { rejectWithValue }) => {
    try {
      await suspenderOcorrenciaProgramada(id, justificativa);
      return { id };
    } catch (err: any) {
      return rejectWithValue(err.message || "Erro ao suspender ocorrência.");
    }
  },
);

export const pausarSerie = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: string }
>("manutencao/pausarSerie", async (id, { rejectWithValue }) => {
  try {
    await pausarSerieManutencao(id);
    return { id };
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao pausar série.");
  }
});

export const retomarSerie = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: string }
>("manutencao/retomarSerie", async (id, { rejectWithValue }) => {
  try {
    await retomarSerieManutencao(id);
    return { id };
  } catch (err: any) {
    return rejectWithValue(err.message || "Erro ao retomar série.");
  }
});

const manutencaoSlice = createSlice({
  name: "manutencao",
  initialState,
  reducers: {
    resetManutencaoState: (state) => {
      state.status = "idle";
      state.error = null;
    },
    setPaginaManutencao: (state, action: PayloadAction<number>) => {
      state.pagina = action.payload;
    },
    setStatusFiltroManutencao: (state, action: PayloadAction<string>) => {
      state.statusFiltro = action.payload;
      state.pagina = 1;
    },
    setPaginaManutencaoEscopo: (state, action: PayloadAction<number>) => {
      state.paginaEscopo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(agendarManutencao.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(agendarManutencao.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(agendarManutencao.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Ocorreu um erro inesperado.";
      })

      .addCase(listarManutencoes.pending, (state) => {
        state.listaStatus = "loading";
        state.listaError = null;
      })
      .addCase(listarManutencoes.fulfilled, (state, action) => {
        state.listaStatus = "idle";
        state.lista = action.payload.dados;
        state.totalRegistros = action.payload.totalRegistros;
        state.pagina = action.payload.pagina;
        state.tamanhoPagina = action.payload.tamanhoPagina;
        state.totalPaginas = action.payload.totalPaginas;
      })
      .addCase(listarManutencoes.rejected, (state, action) => {
        state.listaStatus = "error";
        state.listaError = action.payload ?? "Erro ao carregar a lista.";
      })

      .addCase(aceitarManutencao.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg.id; // era action.meta.arg
      })
      .addCase(aceitarManutencao.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        const item = state.lista.find((m) => m.id === action.payload.id);
        if (item) item.status = "APROVADA";
      })
      .addCase(aceitarManutencao.rejected, (state) => {
        state.actionLoadingId = null;
      })

      .addCase(rejeitarManutencao.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg.id;
      })
      .addCase(rejeitarManutencao.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        const item = state.lista.find((m) => m.id === action.payload.id);
        if (item) item.status = "REJEITADA";
      })
      .addCase(rejeitarManutencao.rejected, (state) => {
        state.actionLoadingId = null;
      })

      .addCase(prorrogarManutencao.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg.id;
      })
      .addCase(prorrogarManutencao.fulfilled, (state) => {
        state.actionLoadingId = null;
      })
      .addCase(prorrogarManutencao.rejected, (state) => {
        state.actionLoadingId = null;
      })

      .addCase(apagarManutencao.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg;
      })
      .addCase(apagarManutencao.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        state.lista = state.lista.filter((m) => m.id !== action.payload.id);
        state.totalRegistros = Math.max(0, state.totalRegistros - 1);
      })
      .addCase(apagarManutencao.rejected, (state) => {
        state.actionLoadingId = null;
      })
      .addCase(suspenderOcorrencia.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg.id;
      })
      .addCase(suspenderOcorrencia.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        const item = state.lista.find((m) => m.id === action.payload.id);
        if (item) item.status = "CANCELADA";
        const itemEscopo = state.listaEscopo.find(
          (m) => m.id === action.payload.id,
        );
        if (itemEscopo) itemEscopo.status = "CANCELADA";
      })
      .addCase(suspenderOcorrencia.rejected, (state) => {
        state.actionLoadingId = null;
      })

      .addCase(pausarSerie.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg;
      })
      .addCase(pausarSerie.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        const item = state.lista.find((m) => m.id === action.payload.id);
        if (item) item.serieAtiva = false;
        const itemEscopo = state.listaEscopo.find(
          (m) => m.id === action.payload.id,
        );
        if (itemEscopo) itemEscopo.serieAtiva = false;
      })
      .addCase(pausarSerie.rejected, (state) => {
        state.actionLoadingId = null;
      })

      .addCase(retomarSerie.pending, (state, action) => {
        state.actionLoadingId = action.meta.arg;
      })
      .addCase(retomarSerie.fulfilled, (state, action) => {
        state.actionLoadingId = null;
        const item = state.lista.find((m) => m.id === action.payload.id);
        if (item) item.serieAtiva = true;
        const itemEscopo = state.listaEscopo.find(
          (m) => m.id === action.payload.id,
        );
        if (itemEscopo) itemEscopo.serieAtiva = true;
      })
      .addCase(retomarSerie.rejected, (state) => {
        state.actionLoadingId = null;
      });
  },
});

export const {
  resetManutencaoState,
  setPaginaManutencao,
  setStatusFiltroManutencao,
  setPaginaManutencaoEscopo,
} = manutencaoSlice.actions;
export default manutencaoSlice.reducer;
